/** A recording that is not ready: what it is doing, or that it failed with a way to try again. Both pages draw it. */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import type { WebinarLive } from "../../../shared/types";
import { boardPath } from "../../board/paths";
import { Button } from "../../components/ui/Button";
import { Icon } from "../../components/ui/Icon";
import { useToast } from "../../components/ui/Toast";
import { ApiError, api } from "../../lib/api";
import { type RecordingPoll, recordingView, restartedPoll, retryOutcome, shouldPollRecording } from "./recording";
import { POLL_MS } from "./useRoomPage";

export function RecordingStrip({ detailId, videoId, state, durationSec, onReady }: {
  detailId: string;
  videoId: string | null;
  state: WebinarLive["replay"]["videoState"];
  durationSec: number;
  onReady: () => void;
}) {
  const navigate = useNavigate();
  const toast = useToast();
  const [poll, setPoll] = useState<RecordingPoll | null>(null);
  const [retrying, setRetrying] = useState(false);
  const [failed, setFailed] = useState<{ message: string; upload: boolean } | null>(null);
  const look = recordingView({ videoId, state, durationSec, poll });
  const watching = shouldPollRecording(look);

  useEffect(() => {
    if (!watching) return;
    const iv = window.setInterval(() => {
      api.webinars
        .videoStatus(detailId)
        .then((s) => {
          setPoll(s);
          if (s.ready) onReady();
        })
        .catch(() => undefined);
    }, POLL_MS);
    return () => window.clearInterval(iv);
  }, [watching, detailId, onReady]);

  if (look.kind === "none" || look.kind === "ready") return null;
  const retry = () => {
    if (retrying) return;
    setRetrying(true);
    setFailed(null);
    api.webinars
      .retryVideo(detailId)
      .then((s) => {
        setPoll(s);
        toast("Making it playable again");
      })
      .catch((e: unknown) => {
        const out = retryOutcome(e instanceof ApiError ? e.status : 0, e instanceof ApiError ? e.message : "");
        if (out.kind === "keepPolling") {
          setPoll(restartedPoll());
          toast(out.toast);
        } else setFailed({ message: out.message, upload: out.kind === "needsUpload" });
      })
      .finally(() => setRetrying(false));
  };

  if (look.kind === "processing") {
    return (
      <div className="strip mt20">
        <div className="ic">
          <Icon name="hourglass" />
        </div>
        <div className="sp">
          <b>{look.label}</b>
          {look.percent !== null ? (
            <div className="bar mt6" style={{ maxWidth: 260 }}>
              <i style={{ width: `${look.percent}%` }} />
            </div>
          ) : null}
        </div>
      </div>
    );
  }
  return (
    <div className="strip warn mt20">
      <div className="ic">
        <Icon name="info" />
      </div>
      <div className="sp">
        <b>{failed?.message ?? look.message}</b>
      </div>
      {failed?.upload ? (
        <Button size="s" variant="p" onClick={() => navigate(boardPath(detailId))}>
          Upload it again
        </Button>
      ) : (
        <Button size="s" variant="p" icon="refresh" disabled={retrying} onClick={retry}>
          {retrying ? "Trying again…" : "Try again"}
        </Button>
      )}
    </div>
  );
}

